import * as React from 'react'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { Skeleton } from '@material-ui/lab'
import WebpImg from 'components/Image/WebpImg'

type Props = {
  id: string
  src: string
  alt?: string
  // 画像のアスペクト比指定のために用いる高さ・幅。実数値で高さ・幅を指定する場合は1つ外側の要素のスタイルで指定する
  height?: number | null
  width?: number | null
  imgWrapperClassName?: string
  pictureClassName?: string
  imgClassName?: string
  aspectRatio?: number // aspectRatio未指定の場合、1つ外側の要素にheightを指定する
  borderRadius?: number | string | null
  borderRadiusOverWidth?: number
  rootStyle?: React.CSSProperties
  onClick?: (e: React.MouseEvent) => void
}

const ITEM_ASPECT_RATIO = 1.57
const ITEM_BORDER_RADIUS_OVER_WIDTH = 0.05

const isSSR = typeof window === 'undefined'

export default React.forwardRef(function (
  props: Props,
  ref: React.Ref<HTMLDivElement>
) {
  const {
    id,
    src,
    alt = '',
    height = null,
    width = null,
    borderRadius = null,
    imgWrapperClassName = '',
    pictureClassName = '',
    imgClassName = '',
    rootStyle = {},
    onClick,
  } = props

  const [isLoaded, setIsLoaded] = React.useState(
    !isSSR &&
      (document.getElementById(`img-${id}`)?.style.opacity === '1' ?? false)
  )
  const onLoad = React.useCallback(() => {
    setIsLoaded(true)
  }, [id])

  const s = useStyle({
    aspectRatio: props.aspectRatio ?? (height && width ? height / width : null),
    borderRadius,
    borderRadiusOverWidth:
      props.borderRadiusOverWidth ?? ITEM_BORDER_RADIUS_OVER_WIDTH,
  })

  return (
    <div
      ref={ref}
      style={{
        ...rootStyle,
        width: rootStyle?.position === 'absolute' ? 'inherit' : null,
      }}
      className={s.imgWithSkeletonRoot}
    >
      {/*
       * Server Side Renderingの際、JSよりも前に画像データが読み込まれる場合がある。
       * その場合、通常のReact DOM要素ではonLoadイベントハンドラが発火しなくなるため、
       * imgタグの部分のみdangerouslySetInnerHtmlを使用して生のDOM要素として渡すことで、
       * hydrate前のHTML単体でも画像が読み込まれ次第スケルトンを非表示にする処理を可能にしている。
       */}
      {isSSR ? (
        <div
          className={`${s.imgWrapper} ${imgWrapperClassName}`}
          dangerouslySetInnerHTML={{
            __html: (() => {
              const heightAttr = height ? `height="${height}"` : ''
              const widthAttr = width ? `width="${width}"` : ''
              return `
              <picture class="${pictureClassName}">
                <img id="img-${id}" src="${src}" alt="${alt}" ${heightAttr} ${widthAttr}
              style="opacity: 0;" class="${s.img} ${imgClassName}" onload="this.setAttribute('style', 'opacity: 1; transition: 0.3s;'); document.getElementById('skeleton-${id}').setAttribute('style', 'visibility: hidden');">
              </picture>
            `
            })(),
          }}
        />
      ) : (
        <div className={`${s.imgWrapper} ${imgWrapperClassName}`}>
          <WebpImg
            className={`${s.img} ${imgClassName}`}
            style={
              isLoaded ? { opacity: 1, transition: '0.3s' } : { opacity: 0 }
            }
            pictureClassName={pictureClassName}
            imgSrc={src}
            imgProps={{ id: `img-${id}`, alt, height, width, onLoad, onClick }}
            sourceProps={{ id: `img-${id}`, onLoad, onClick }}
            sourceStyle={{ height: 'auto' }}
          />
        </div>
      )}
      <Skeleton
        id={`skeleton-${id}`}
        variant="rect"
        style={isLoaded && { visibility: 'hidden' }}
        className={s.skeleton}
      />
    </div>
  )
})

const toPercent = (ratio: number): string => `${ratio * 100}%`

const useStyle = makeStyles<
  Theme,
  {
    aspectRatio?: number
    borderRadius?: number | string
    borderRadiusOverWidth: number
  }
>({
  imgWithSkeletonRoot: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  imgWrapper: {
    position: 'absolute',
    height: 'inherit',
    width: 'inherit',
  },
  img: {
    width: '100%',
    height: '100%',
    margin: '0 auto',
    display: 'block',
  },
  skeleton: {
    position: 'relative',
    height: ({ aspectRatio }) => (aspectRatio ? 0 : '100%'),
    width: '100%',
    margin: '0 auto',
    paddingTop: ({ aspectRatio }) => (aspectRatio ? toPercent(aspectRatio) : 0),
    borderRadius: ({ aspectRatio, borderRadius, borderRadiusOverWidth }) =>
      borderRadius ??
      `${toPercent(borderRadiusOverWidth * aspectRatio)} / ${toPercent(
        borderRadiusOverWidth
      )}`,
  },
})
